import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { GoogleLoginButton } from '@/components/GoogleLoginButton';
import { toast } from 'sonner';
import { GraduationCap, Flame, Shield, Timer, Sparkles, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back!');
      nav(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-black text-white">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            'radial-gradient(900px circle at 10% 10%, rgba(220,38,38,0.28), transparent 55%), radial-gradient(700px circle at 90% 20%, rgba(239,68,68,0.16), transparent 60%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        }}
      />

      <div className="relative z-10">
        <header className="border-b border-white/10">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_28px_rgba(239,68,68,0.55)]">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-base font-semibold">StudyTrack AI</div>
                <div className="text-[11px] text-white/50">NCERT · JEE · NEET</div>
              </div>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </div>
        </header>

        <div className="mx-auto flex min-h-[calc(100vh-64px)] max-w-6xl items-center justify-center px-4 py-10 sm:px-6">
          <div className="grid w-full max-w-5xl gap-10 md:grid-cols-2">
            <div className="hidden flex-col justify-center gap-5 md:flex">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
                <Flame className="h-3.5 w-3.5" />
                Welcome back, grinder.
              </div>
              <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl">
                Pick up where you{' '}
                <span className="bg-gradient-to-b from-red-400 to-red-600 bg-clip-text text-transparent">
                  left off.
                </span>
              </h1>
              <p className="max-w-md text-white/70">
                Your chapter mocks, timers, and AI report cards are one login away.
              </p>
              <ul className="space-y-2 text-sm text-white/70">
                <li className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-red-400" />
                  Real exam-hall timer
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-red-400" />
                  Fresh AI mocks every attempt
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-red-400" />
                  100% NCERT-aligned
                </li>
              </ul>
            </div>

            <div className="w-full rounded-2xl border border-white/10 bg-black/60 p-6 shadow-2xl backdrop-blur sm:p-8">
              <div className="mb-6">
                <div className="font-display text-2xl font-semibold">Log in</div>
                <div className="mt-1 text-sm text-white/60">Continue your exam prep.</div>
              </div>
              <form className="space-y-4" onSubmit={onSubmit}>
                <GoogleLoginButton testId="login-google-button" />
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/15" />
                  <span className="text-[11px] uppercase tracking-widest text-white/40">or continue with email</span>
                  <div className="h-px flex-1 bg-white/15" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-white/80">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="h-11 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-red-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-white/80">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11 border-white/15 bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:ring-red-500"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 w-full bg-red-600 font-semibold text-white shadow-[0_0_28px_rgba(239,68,68,0.45)] hover:bg-red-500"
                  data-testid="login-submit-button"
                >
                  {loading ? 'Signing in…' : 'Log in'}
                </Button>
                <p className="text-sm text-white/60">
                  New here?{' '}
                  <Link
                    to="/register"
                    className="text-red-400 underline-offset-2 hover:underline"
                    data-testid="go-to-register-link"
                  >
                    Create an account
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
